import { todayKey } from './cancellationPolicy';

/**
 * El día del SERVICIO en la pantalla: el encabezado que agrupa "Próximas a
 * viajar" y la fecha que lleva cada fila.
 *
 * `serviceDate` es una fecha de CALENDARIO (`YYYY-MM-DD`, sin zona): el día en
 * que se presta el servicio donde se presta. Por eso nunca se construye con
 * `new Date('2026-09-19')` —que la lee como medianoche UTC y en cualquier
 * huso al oeste la corre un día para atrás, mostrando "viernes 18" un servicio
 * del sábado 19— sino con sus tres números en el calendario local, que es el
 * mismo criterio con el que se compara contra el día del asesor (`todayKey`,
 * la única definición de "hoy" del feature).
 */

/** La fecha de calendario como Date LOCAL, o null si no es una. */
export function serviceDayDate(dateKey?: string | null): Date | null {
  if (typeof dateKey !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey.trim());
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // Un `2026-02-31` se desbordaría al 3 de marzo: se descarta en vez de
  // dibujar un día que nadie pidió.
  return date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)
    ? date
    : null;
}

/** El día siguiente a una fecha de calendario, en su misma forma. */
export function nextServiceDayKey(dateKey: string): string | null {
  const date = serviceDayDate(dateKey);
  if (!date) return null;
  date.setDate(date.getDate() + 1);
  return todayKey(date);
}

/**
 * "Sáb 19 sep" en la fila, "sábado 19 de septiembre" en el encabezado del día:
 * el mismo día escrito con dos densidades, pero UNA sola derivación de la
 * fecha (§21.22). Con dos, el encabezado podría nombrar un día y la fila que
 * cuelga de él, otro.
 */
export const SERVICE_DAY_FORMATS = {
  short: { weekday: 'short', day: 'numeric', month: 'short' },
  long: { weekday: 'long', day: 'numeric', month: 'long' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export function serviceDayLabel(
  dateKey: string,
  locale: string,
  format: keyof typeof SERVICE_DAY_FORMATS = 'short',
): string | null {
  const date = serviceDayDate(dateKey);
  if (!date) return null;
  return date.toLocaleDateString(locale, SERVICE_DAY_FORMATS[format]);
}

/**
 * Cómo se rotula el encabezado de un día. "Hoy" y "mañana" son las dos únicas
 * fechas que el operador piensa por su nombre; el resto se lee por su fecha.
 * `undated` no debería llegar a "próximas" —el endpoint exige fecha ahí— pero
 * existe para que una fila sin fecha se DIBUJE igual en vez de desaparecer del
 * grupo y de la pantalla.
 */
export type BookingDayGroupKind = 'today' | 'tomorrow' | 'date' | 'undated';

export interface BookingDayGroup<T> {
  /** `YYYY-MM-DD`, o `''` cuando el grupo es el de las que no tienen fecha. */
  dateKey: string;
  kind: BookingDayGroupKind;
  bookings: T[];
}

export function bookingDayGroupKind(
  dateKey: string,
  today: string,
): BookingDayGroupKind {
  if (dateKey === today) return 'today';
  return nextServiceDayKey(today) === dateKey ? 'tomorrow' : 'date';
}

/**
 * Las reservas agrupadas por día de servicio, RESPETANDO el orden en que
 * llegan: el endpoint ya las ordena por fecha y hora, así que reordenar acá
 * sería una segunda definición del orden de la pestaña. Los grupos salen en el
 * orden de su primera fila y las sin fecha van al final, juntas.
 */
export function groupBookingsByServiceDay<
  T extends { serviceDate?: string | null },
>(bookings: T[], today: string): BookingDayGroup<T>[] {
  const groups: BookingDayGroup<T>[] = [];
  const byKey = new Map<string, BookingDayGroup<T>>();
  const undated: T[] = [];

  bookings.forEach((booking) => {
    const dateKey = serviceDayDate(booking.serviceDate)
      ? (booking.serviceDate as string).trim()
      : null;
    if (!dateKey) {
      undated.push(booking);
      return;
    }
    const existing = byKey.get(dateKey);
    if (existing) {
      existing.bookings.push(booking);
      return;
    }
    const group: BookingDayGroup<T> = {
      dateKey,
      kind: bookingDayGroupKind(dateKey, today),
      bookings: [booking],
    };
    byKey.set(dateKey, group);
    groups.push(group);
  });

  if (undated.length > 0) {
    groups.push({ dateKey: '', kind: 'undated', bookings: undated });
  }
  return groups;
}

/**
 * CUÁNDO se presta el servicio, tal como lo lee la fila: el día (por su nombre
 * cuando es hoy o mañana, por su fecha si no) y la hora de PARED del lugar —
 * `serviceTime` no es un instante y no se convierte a la zona del navegador.
 *
 * Sin fecha publicada el momento es `undated` y la fila muestra un guion: hay
 * reservas que no tienen día por naturaleza (una eSIM arranca al escanear el
 * QR), y ahí inventar una fecha sería peor que no decir nada.
 */
export interface BookingServiceMoment {
  kind: BookingDayGroupKind;
  dateKey: string;
  /** Ya formateado; presente solo cuando el día se lee por su fecha. */
  dateLabel?: string;
  /** `HH:mm` del lugar, si el hub lo publica. */
  time?: string;
}

const WALL_CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export function bookingServiceMoment(
  booking: { serviceDate?: string | null; serviceTime?: string | null },
  today: string,
  locale: string,
): BookingServiceMoment {
  const time =
    typeof booking.serviceTime === 'string' &&
    WALL_CLOCK_TIME.test(booking.serviceTime.trim())
      ? booking.serviceTime.trim()
      : undefined;
  const dateKey = serviceDayDate(booking.serviceDate)
    ? (booking.serviceDate as string).trim()
    : null;
  if (!dateKey) {
    return { kind: 'undated', dateKey: '', ...(time ? { time } : {}) };
  }
  const kind = bookingDayGroupKind(dateKey, today);
  return {
    kind,
    dateKey,
    ...(kind === 'date'
      ? { dateLabel: serviceDayLabel(dateKey, locale) ?? dateKey }
      : {}),
    ...(time ? { time } : {}),
  };
}

export { todayKey };
